import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, RotateCcw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useSelector } from 'react-redux';
import CommonLoader from '../CommonLoader';
import { useTranslation } from 'react-i18next';

const AppointmentYearGraph = ({
  specialtyId,
  addressTypeName,
  monthWiseAppointments,
  loading,
  selectedYear,
  onYearChange,
  embedded,
} = {}) => {
  const { t } = useTranslation('dashboard');
  const { selectedDoctor } = useSelector((state) => state.doctor);

  const [visibleLines, setVisibleLines] = useState({
    completed: true,
    cancelled: true,
    missed: true,
  });

  // Generate years based on doctor creation date
  const getYearRange = () => {
    const currentYear = new Date().getFullYear();
    if (!selectedDoctor?.createdAt) {
      return [currentYear];
    }
    const createdYear = new Date(selectedDoctor.createdAt).getFullYear();
    const yearCount = currentYear - createdYear + 1;
    return Array.from({ length: yearCount }, (_, i) => createdYear + i);
  };

  const years = getYearRange();

  // Helper function to abbreviate month names
  const abbreviateMonth = (monthName) => {
    return monthName.substring(0, 3);
  };

  // Get current year data from API
  const getYearData = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-11
    const createdYear = selectedDoctor?.createdAt ? new Date(selectedDoctor.createdAt).getFullYear() : currentYear;
    const createdMonth = selectedDoctor?.createdAt ? new Date(selectedDoctor.createdAt).getMonth() : 0; // 0-11

    // Determine the starting month for the selected year
    let startMonth = 0; // January
    if (selectedYear === createdYear) {
      startMonth = createdMonth;
    }

    // Determine the ending month for the selected year
    let endMonth = 11; // December
    if (selectedYear === currentYear) {
      endMonth = currentMonth;
    }

    const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    if (monthWiseAppointments?.data) {
      // Filter data to only include entries for the selected year
      const yearData = monthWiseAppointments.data.filter((item) => item.year === selectedYear);
      
      // Create a map of month names to data
      const monthMap = {};
      yearData.forEach((item) => {
        monthMap[item.monthName] = {
          totalAppointments: item.totalAppointments,
          completed: item.completed,
          cancelled: item.cancelled,
          missed: item.missed
        };
      });

      // Generate only valid months (from startMonth to endMonth)
      const monthlyData = [];
      for (let i = startMonth; i <= endMonth; i++) {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];
        const monthName = monthNames[i];
        
        const data = monthMap[monthName] || { totalAppointments: 0, completed: 0, cancelled: 0, missed: 0 };
        monthlyData.push({ 
          month: monthAbbreviations[i], 
          appointments: data.totalAppointments,
          completed: data.completed,
          cancelled: data.cancelled,
          missed: data.missed,
          color: '#4ABDBD'
        });
      }

      return {
        total: monthWiseAppointments.totalAppointments || 0,
        average: monthWiseAppointments.averageMonthly || 0,
        monthlyData: monthlyData,
        startMonth,
        endMonth
      };
    }

    // Default empty data only for valid months
    const monthlyData = [];
    for (let i = startMonth; i <= endMonth; i++) {
      monthlyData.push({ 
        month: monthAbbreviations[i], 
        appointments: 0,
        completed: 0,
        cancelled: 0,
        missed: 0,
        color: '#4ABDBD' 
      });
    }
    
    return {
      total: 0,
      average: 0,
      monthlyData: monthlyData,
      startMonth,
      endMonth
    };
  };

  const currentYearData = getYearData();

  const chartData = currentYearData.monthlyData.map(item => ({
    month: item.month,
    total: item.appointments,
    completed: item.completed,
    cancelled: item.cancelled,
    missed: item.missed,
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const monthData = chartData.find(d => d.month === label);
      if (!monthData) return null;
      return (
        <div data-testid="appointment-year-graph-tooltip" className="bg-white p-2 border border-gray-200 rounded shadow-lg">
          <p data-testid="appointment-year-graph-tooltip-month" className="font-semibold text-gray-900 mb-1">{label}</p>
          <p data-testid="appointment-year-graph-tooltip-total" className="text-xs text-gray-700">{t('dashboard.total_label', 'Total')}: {monthData.total} {t('dashboard.appointments', 'appointments')}</p>
          <p data-testid="appointment-year-graph-tooltip-completed" className="text-xs" style={{ color: '#51a2ff' }}>{t('dashboard.completed', 'Completed')}: {monthData.completed}</p>
          <p data-testid="appointment-year-graph-tooltip-cancelled" className="text-xs" style={{ color: '#EF4444' }}>{t('dashboard.cancelled', 'Cancelled')}: {monthData.cancelled}</p>
          <p data-testid="appointment-year-graph-tooltip-missed" className="text-xs" style={{ color: '#F59E0B' }}>{t('dashboard.missed', 'Missed')}: {monthData.missed}</p>
        </div>
      );
    }
    return null;
  };

  const handleResetFilters = () => {
    onYearChange(new Date().getFullYear());
  };

  const toggleLine = (lineKey) => {
    setVisibleLines(prev => ({
      ...prev,
      [lineKey]: !prev[lineKey]
    }));
  };

  const legendConfig = [
    { key: 'completed', label: t('dashboard.legend_completed'), color: '#51a2ff' },
    { key: 'cancelled', label: t('dashboard.legend_cancelled'), color: '#EF4444' },
    { key: 'missed', label: t('dashboard.legend_missed'), color: '#F59E0B' },
  ];

  return (
    <motion.div
      data-testid="appointment-year-graph"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className={embedded ? "w-full" : "bg-white w-full rounded-xl p-4 border border-gray-200/50 shadow-md"}
    >
      <div data-testid="appointment-year-graph-header" className={embedded ? "hidden" : "flex flex-col items-start gap-3 mb-4"}>
        <h3 data-testid="appointment-year-graph-title" className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
          <Calendar data-testid="calendar-icon" className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
          {t('dashboard.monthwise_appointments')}
        </h3>
        {/* <div data-testid="appointment-year-graph-controls" className="flex flex-col gap-3 w-full">
          <div className="flex gap-2 w-full">
            <select
              data-testid="appointment-year-graph-year-select"
              value={selectedYear}
              onChange={(e) => onYearChange(parseInt(e.target.value))}
              className="tt-select text-xs sm:text-sm text-gray-700 rounded-lg px-3 py-2 border border-gray-200/50 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/20 w-40 shadow-sm"
            >
              {years.map(year => (
                <option key={year} value={year} data-testid={`appointment-year-graph-year-option-${year}`}>{year}</option>
              ))}
            </select>
            <motion.button
              data-testid="appointment-year-graph-reset-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleResetFilters}
              className="flex items-center justify-center text-xs sm:text-sm font-semibold bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-lg px-3 py-2 border border-gray-200/50 hover:from-[#4ABDBD]/10 hover:to-[#3A9B9B]/10 hover:text-[#4ABDBD] w-fit shadow-sm transition-all duration-300"
            >
              <RotateCcw data-testid="appointment-year-graph-reset-icon" className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              {t('dashboard.reset')}
            </motion.button>
          </div>
        </div> */}
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <div data-testid="appointment-year-graph-loading" className="flex flex-col items-center justify-center py-12">
            <CommonLoader />
            <span data-testid="appointment-year-graph-loading-text" className="text-sm text-gray-600 mt-2">{t('dashboard.loading_appointments_data')}</span>
          </div>
        ) : (
        <motion.div
          key={selectedYear}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="space-y-4"
        >
          <div data-testid="appointment-year-graph-stats-row" className="flex items-center justify-between p-4 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3A9B9B]/5 rounded-lg">
            <div data-testid="appointment-year-graph-total-card">
              <p data-testid="appointment-year-graph-total-label" className="text-sm font-medium text-gray-600">{t('dashboard.total_appointments_year', { year: selectedYear })}</p>
              <p data-testid="appointment-year-graph-total-value" className="text-2xl font-bold text-gray-900">
                {currentYearData.total}
              </p>
            </div>
            <div data-testid="appointment-year-graph-average-card" className="text-right">
              <p data-testid="appointment-year-graph-average-label" className="text-sm font-medium text-gray-600">{t('dashboard.average_monthly')}</p>
              <p data-testid="appointment-year-graph-average-value" className="text-lg font-semibold text-[#4ABDBD]">
                  {currentYearData.average}
              </p>
            </div>
          </div>

            {currentYearData.total > 0 ? (
          <div data-testid="appointment-year-graph-content">
            {/* Custom Interactive Legend */}
            <div data-testid="appointment-year-graph-legend" className="flex flex-wrap items-center justify-center gap-4 mb-2">
              {legendConfig.map(({ key, label, color }) => (
                <button
                  key={key}
                  data-testid={`appointment-year-graph-legend-toggle-${key}`}
                  onClick={() => toggleLine(key)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-all duration-200 cursor-pointer ${
                    visibleLines[key]
                      ? 'bg-white shadow-sm hover:shadow-md border border-gray-200'
                      : 'bg-gray-100 opacity-50 hover:opacity-70'
                  }`}
                >
                  <div
                    data-testid={`appointment-year-graph-legend-color-${key}`}
                    className={`w-8 h-0.5 rounded-full transition-opacity ${
                      visibleLines[key] ? 'opacity-100' : 'opacity-30'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                  <span
                    data-testid={`appointment-year-graph-legend-label-${key}`}
                    className={`text-xs font-medium transition-colors ${
                      visibleLines[key] ? 'text-gray-700' : 'text-gray-400'
                    }`}
                  >
                    {label}
                  </span>
                </button>
              ))}
            </div>

            {/* Chart */}
            <div data-testid="appointment-year-graph-chart" className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="0" stroke="transparent" />
                  <XAxis 
                    dataKey="month" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {visibleLines.completed && (
                    <Line 
                      type="monotone" 
                      dataKey="completed" 
                      stroke="#51a2ff" 
                      strokeWidth={3}
                      dot={false}
                    />
                  )}
                  {visibleLines.cancelled && (
                    <Line 
                      type="monotone" 
                      dataKey="cancelled" 
                      stroke="#EF4444" 
                      strokeWidth={3}
                      dot={false}
                    />
                  )}
                  {visibleLines.missed && (
                    <Line 
                      type="monotone" 
                      dataKey="missed" 
                      stroke="#F59E0B" 
                      strokeWidth={3}
                      dot={false}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
            ) : (
              <div data-testid="appointment-year-graph-empty-state" className="h-80 flex flex-col items-center justify-center">
                <Calendar data-testid="appointment-year-graph-empty-icon" className="w-12 h-12 text-gray-300 mb-2" />
                <p data-testid="appointment-year-graph-empty-text" className="text-sm text-gray-600">{t('dashboard.no_appointments_for_year', { year: selectedYear })}</p>
              </div>
            )}
        </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AppointmentYearGraph;
